import { axiosInstance } from 'utils/http';
import { hosts } from 'utils/urlConstants';
import { useMutation, useQuery } from 'react-query';

// =============================================================================
// FLOW PROVISIONING SERVICE - Unified Interface
// =============================================================================

/**
 * Hook to provision flows to entities (accounts, organizations, clean rooms)
 */
export const useProvisionFlows = () => useMutation(
  ({ orgId, provisionRequests }) => axiosInstance.post(
    `${hosts.unhygienix}/flow-management/provision`,
    { provisionRequests },
    {
      headers: {
        'organization-id': orgId,
      },
    },
  ),
);

/**
 * Hook to deprovision a flow from an entity
 */
export const useDeprovisionFlow = () => useMutation(({
  orgId,
  entityType,
  entityId,
  flowId,
}) => axiosInstance.delete(
  `${hosts.unhygienix}/flow-management/deprovision`,
  {
    data: {
      entityType,
      entityId,
      flowId,
    },
    headers: {
      'organization-id': orgId,
    },
  },
));

/**
 * Hook to request a flow for an entity
 */
export const useRequestFlow = () => useMutation(({
  orgId,
  entityType,
  entityId,
  flowId,
}) => axiosInstance.post(
  `${hosts.unhygienix}/flow-management/request`,
  {
    entityType,
    entityId,
    flowId,
  },
  {
    headers: {
      'organization-id': orgId,
    },
  },
));

// =============================================================================
// FLOW LISTING HOOKS
// =============================================================================

/**
 * Hook to list organization available flows
 */
export const useListOrganizationAvailableFlows = (organizationId, options = {}) => useQuery(
  ['organizationAvailableFlows', organizationId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/organization/${organizationId}/flow-management/available`,
  ),
  {
    enabled: !!organizationId,
    ...options,
  },
);

/**
 * Hook to list organization provisioned flows (includes clean room flows)
 */
export const useListOrganizationProvisionedFlows = (
  accountId,
  organizationId,
  options = {},
) => useQuery(
  ['organizationProvisionedFlowsWithCleanRooms', accountId, organizationId],
  async () => {
    const response = await axiosInstance.post(
      `${hosts.unhygienix}/flow-management/account/${accountId}/organization/${organizationId}/provisioned-list`,
    );
    return response.data;
  },
  {
    enabled: !!accountId && !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  },
);

/**
 * Hook to list account provisioned flows (includes clean room flows from all organizations)
 */
export const useListAccountProvisionedFlows = (accountId, options = {}) => useQuery(
  ['accountProvisionedFlowsWithCleanRooms', accountId],
  async () => {
    const response = await axiosInstance.post(
      `${hosts.unhygienix}/flow-management/account/${accountId}/list`,
    );
    return response.data;
  },
  {
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  },
);

/**
 * Hook to list clean room provisioned flows
 */
export const useListCleanRoomProvisionedFlows = (organizationId, options = {}) => useQuery(
  ['cleanRoomProvisionedFlows', organizationId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/organization/${organizationId}/flow-management/clean-room/provisioned`,
  ),
  {
    enabled: !!organizationId,
    ...options,
  },
);

/**
 * Hook to list platform available flows (admin only)
 */
export const useListPlatformAvailableFlows = (filters = {}, options = {}) => useQuery(
  ['platformAvailableFlows', filters],
  () => axiosInstance.post(`${hosts.unhygienix}/flow-management/list`, filters),
  {
    ...options,
  },
);

// =============================================================================
// ENTITY FETCHING HOOKS
// =============================================================================

/**
 * Hook to fetch entities provisioned for a flow
 */
export const useFetchFlowProvisionedEntities = (organizationId, flowId, options = {}) => useQuery(
  ['flowProvisionedEntities', organizationId, flowId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/organization/${organizationId}/flow-management/flow/${flowId}/entities`,
  ),
  {
    enabled: !!(organizationId && flowId),
    ...options,
  },
);

/**
 * Hook to fetch clean rooms provisioned for a flow
 */
export const useFetchCleanRoomsProvisionedFlows = (
  organizationId,
  flowId,
  options = {},
) => useQuery(
  ['cleanRoomsProvisionedForFlow', organizationId, flowId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/organization/${organizationId}/flow-management/flow/${flowId}/clean-rooms`,
  ),
  {
    enabled: !!(organizationId && flowId),
    ...options,
  },
);

/**
 * Hook to fetch all accounts and organizations provisioned for a flow (admin only)
 */
export const useFetchAccountsAndOrganizationsProvisionedFlows = (flowId, options = {}) => useQuery(
  ['accountsAndOrgsProvisionedForFlow', flowId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/flow-management/flow/${flowId}/accounts-organizations`,
  ),
  {
    enabled: !!flowId,
    ...options,
  },
);

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to get available entities for flow provisioning
 */
export const useAvailableEntitiesForFlows = (organizationId, options = {}) => useQuery(
  ['availableEntitiesForFlows', organizationId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/organization/${organizationId}/flow-management/available-entities`,
  ),
  {
    enabled: !!organizationId,
    ...options,
  },
);

/**
 * Hook to get flow management filters
 */
export const useGetFlowManagementFilters = (
  organizationId,
  filterType = 'organization',
  options = {},
) => {
  const filterEndpoints = {
    platform: `${hosts.unhygienix}/flow-management/filters`,
    account: (accountId) => `${hosts.unhygienix}/account/${accountId}/flow-management/filters`,
    organization: (orgId) => `${hosts.unhygienix}/organization/${orgId}/flow-management/filters`,
    cleanRoom: (orgId) => `${hosts.unhygienix}/organization/${orgId}/flow-management/clean-room/filters`,
  };

  const getUrl = () => {
    if (filterType === 'platform') return filterEndpoints.platform;
    if (filterType === 'account') return filterEndpoints.account(organizationId);
    if (filterType === 'cleanRoom') return filterEndpoints.cleanRoom(organizationId);
    return filterEndpoints.organization(organizationId);
  };

  return useQuery(
    ['flowManagementFilters', filterType, organizationId],
    () => axiosInstance.get(getUrl()),
    {
      enabled: filterType === 'platform' || !!organizationId,
      ...options,
    },
  );
};

/**
 * Hook to get flow management details
 */
export const useGetFlowManagementDetails = (organizationId, flowId, options = {}) => useQuery(
  ['flowManagementDetails', organizationId, flowId],
  () => axiosInstance.get(
    `${hosts.unhygienix}/organization/${organizationId}/flow-management/flow/${flowId}`,
  ),
  {
    enabled: !!(organizationId && flowId),
    ...options,
  },
);

// =============================================================================
// EXPORT ALL HOOKS
// =============================================================================

export default {
  // Core provisioning operations
  useProvisionFlows,
  useDeprovisionFlow,
  useRequestFlow,

  // Listing operations
  useListOrganizationAvailableFlows,
  useListOrganizationProvisionedFlows,
  useListAccountProvisionedFlows,
  useListCleanRoomProvisionedFlows,
  useListPlatformAvailableFlows,

  // Entity fetching operations
  useFetchFlowProvisionedEntities,
  useFetchCleanRoomsProvisionedFlows,
  useFetchAccountsAndOrganizationsProvisionedFlows,

  // Utility operations
  useAvailableEntitiesForFlows,
  useGetFlowManagementFilters,
  useGetFlowManagementDetails,
};
